// import components
import { Button, Menu, MenuItem, SxProps } from '@mui/material';

// import enums
import { ViewContext, View } from '../../enums';

// import hooks
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import usePlanningEntity, {
  PlanningEntityTypePreference
} from '../../hooks/usePlanningEntity';

// import types
import { PlanningEntityType } from 'generated';

// import utils
import { getEntityPreferenceKey, getViewContext } from '../../utils';
import { Icon } from '@sdb/planning-ui';

interface Props {
  view: View;
  disabled?: boolean;
  sx?: SxProps;
}

export default function EntityMenu({ view, disabled, sx }: Props) {
  const {
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasActivityPlanningViewAccess,
    hasKidsPlanAccess,
    hasProjectTeamAccess
  } = useSelector(({ grants }) => grants);
  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning.planningEntityType'
  });
  const [entity, setEntity] = usePlanningEntity();
  const entityKey = getEntityPreferenceKey(view);
  const selectedEntity = entity[entityKey];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const context = getViewContext();

  // Memoised so the bootstrap effect's dep array is stable across renders;
  // without this the effect was re-firing every render because the array
  // literal was reconstructed each time.
  const planningEntityTypes = useMemo<PlanningEntityType[]>(() => {
    const types: PlanningEntityType[] = [];
    if (
      (hasMyActivitiesProjectAccess && context === ViewContext.Own) ||
      ((hasManageOwnProjectsAccess || hasProjectTeamAccess) &&
        context === ViewContext.Team &&
        view != View.Timeline)
    ) {
      types.push('PROJECT');
    }
    if (hasWorkPlacePlanningViewAccess && view != View.Timeline) {
      types.push('WORKPLACE');
    }
    if (hasActivityPlanningViewAccess && view == View.Timeline) {
      types.push('ROUTE');
    }
    if (
      hasActivityPlanningViewAccess ||
      hasKidsPlanAccess ||
      (hasProjectTeamAccess && view === View.List)
    ) {
      types.push('EMPLOYEE');
    }
    if (hasActivityPlanningViewAccess && view != View.Timeline) {
      types.push('CLIENT');
    }

    // My Activities only shows WORKPLACE and PROJECT.
    if (context === ViewContext.Own) {
      return types.filter(
        (t: PlanningEntityType) => t === 'WORKPLACE' || t === 'PROJECT'
      );
    }
    return types;
  }, [
    context,
    view,
    hasActivityPlanningViewAccess,
    hasKidsPlanAccess,
    hasManageOwnProjectsAccess,
    hasMyActivitiesProjectAccess,
    hasProjectTeamAccess,
    hasWorkPlacePlanningViewAccess
  ]);

  useEffect(() => {
    if (!planningEntityTypes.includes(selectedEntity)) {
      setEntity({
        [entityKey]: planningEntityTypes[0]
      } as PlanningEntityTypePreference);
    }
  }, [entityKey, planningEntityTypes, selectedEntity, setEntity]);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        disabled={disabled}
        sx={{
          display: 'flex',
          gap: (theme) => theme.spacing(2),
          zIndex: 31,
          overflow: 'hidden',
          ...(sx || {})
        }}
      >
        <Icon type="sliders" fontSize="small" />
        {t(selectedEntity)}
        {!disabled && <Icon type="chevron-down" fontSize="small" />}
      </Button>
      {!disabled && (
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {planningEntityTypes.map((entity: PlanningEntityType, i: number) => (
            <MenuItem
              key={i}
              onClick={() => {
                setEntity({
                  [entityKey]: entity
                } as PlanningEntityTypePreference);
                handleClose();
              }}
            >
              {t(entity)}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
